import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, KeyRound, Loader2, Power } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function InviteCodesPanel() {
  const [codes, setCodes] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/invite-codes").then((r) => setCodes(r.data)).catch(() => setCodes([])), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/invite-codes/generate", { note });
      setNote("");
      await load();
      navigator.clipboard?.writeText(r.data.code).catch(() => {});
      toast.success(`Invite code ${r.data.code} created and copied`);
    } catch (e) { toast.error("Could not create an invite code."); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/invite-codes/${c.id}`, { is_active: !c.is_active });
      await load();
      toast.success(c.is_active ? `${c.code} deactivated` : `${c.code} is active again`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the code."); }
  };

  const copy = (code) => { navigator.clipboard?.writeText(code).catch(() => {}); toast.success(`Copied ${code}`); };

  const status = (c) => c.used_by_user_id ? ["Used", "text-[#34D399] border-[#34D399]/40"] : c.is_active ? ["Available", "text-[#22D3EE] border-[#22D3EE]/40"] : ["Deactivated", "text-slate-400 border-white/15"];
  const available = (codes || []).filter((c) => c.is_active && !c.used_by_user_id).length;

  return (
    <div className="space-y-6" data-testid="invite-codes-panel">
      <div className="hq-glass rounded-2xl p-5 border border-[#22D3EE]/30">
        <h3 className="font-display text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#22D3EE]" /> New invite code</h3>
        <p className="text-sm text-muted-foreground mt-1">Single-use. A teacher enters it when choosing “Guide” at sign-up, or in their trial console to upgrade to a full account (classes and progress are kept).</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Input data-testid="invite-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional) — e.g. Ms. Lopez, Northgate MS" className="bg-white/5 border-white/10" />
          <Button data-testid="generate-invite-btn" onClick={generate} disabled={busy} className="bg-[#22D3EE] text-[#04121f] hover:bg-[#67E8F9] shrink-0">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate code"}</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3" data-testid="invite-available-count">{available} available · {(codes || []).filter((c) => c.used_by_user_id).length} used · {(codes || []).length} total</p>
      </div>

      <div className="hq-glass rounded-2xl border border-white/10 overflow-auto max-h-[70vh]">
        {!codes ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <table className="w-full text-sm" data-testid="invite-codes-table">
            <thead className="sticky top-0 bg-[#0b1220] text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>{["Code", "Status", "Note", "Used by", "Used on", "Created", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {codes.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No invite codes yet — generate your first one above.</td></tr>}
              {codes.map((c) => {
                const [label, cls] = status(c);
                return (
                  <tr key={c.id} data-testid={`invite-row-${c.code}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                    <td className="px-4 py-3"><button data-testid={`invite-copy-${c.code}`} onClick={() => copy(c.code)} className="inline-flex items-center gap-2 font-mono-data tracking-widest text-slate-100 hover:text-[#22D3EE]">{c.code}<Copy className="w-3.5 h-3.5 opacity-60" /></button></td>
                    <td className="px-4 py-3"><span data-testid={`invite-status-${c.code}`} className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>{label}</span></td>
                    <td className="px-4 py-3 text-slate-300">{c.note || "—"}</td>
                    <td className="px-4 py-3">{c.used_by_user_id ? <><p>{c.used_by_name || "Guide"}{c.upgraded_from_trial && <span className="ml-2 text-[10px] text-primary">from trial</span>}</p><p className="text-xs text-muted-foreground">{c.used_by_email}</p></> : "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(c.used_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(c.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {!c.used_by_user_id && (
                        <Button size="sm" variant="outline" data-testid={`invite-toggle-${c.code}`} onClick={() => toggle(c)} className={`gap-1.5 ${c.is_active ? "" : "border-[#34D399]/50 text-[#34D399]"}`}>
                          <Power className="w-3.5 h-3.5" /> {c.is_active ? "Deactivate" : "Activate"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
